"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import type { LucideIcon } from "lucide-react";
import { CreditCard, Crown, UserCheck, UserX, XCircle } from "lucide-react";
import { Fragment } from "react";

export type RegistrantUserActionKey =
  | "billing"
  | "makePrimary"
  | "disable"
  | "reenable"
  | "cancelInvite";

interface RegistrantUserAction {
  key: RegistrantUserActionKey;
  label: string;
  icon: LucideIcon;
  variant?: "destructive";
  /** Visual break before this item in the list menu, matching grouping there. */
  beforeSeparator?: boolean;
}

interface RegistrantUserActionsModelProps {
  user: RegistrantAccountUserResponseSchema;
  paymentAccountId?: string;
  omit?: RegistrantUserActionKey[];
}

/** Same visibility, labels, and icons for the users list and the details pane. */
export function getRegistrantUserActions({
  user,
  paymentAccountId,
  omit,
}: RegistrantUserActionsModelProps): RegistrantUserAction[] {
  return buildRegistrantUserActions({ user, paymentAccountId }).filter(
    (action) => !omit?.includes(action.key),
  );
}

function buildRegistrantUserActions({
  user,
  paymentAccountId,
}: RegistrantUserActionsModelProps): RegistrantUserAction[] {
  if (user.status === "pending") {
    return [
      {
        key: "cancelInvite",
        label: "Cancel invite",
        icon: XCircle,
        variant: "destructive",
      },
    ];
  }

  if (user.status === "disabled") {
    return [
      {
        key: "reenable",
        label: "Re-enable",
        icon: UserCheck,
      },
    ];
  }

  if (user.status !== "active") {
    return [];
  }

  const isSecondary = Boolean(user.primary_id);
  const actions: RegistrantUserAction[] = [];

  if (isSecondary) {
    actions.push({
      key: "makePrimary",
      label: "Make Primary",
      icon: Crown,
      beforeSeparator: true,
    });
  }

  if (paymentAccountId !== user.id) {
    actions.push({
      key: "billing",
      label: "Set as Billing Account",
      icon: CreditCard,
    });
  }

  if (isSecondary) {
    actions.push({
      key: "disable",
      label: "Disable",
      icon: UserX,
      variant: "destructive",
      beforeSeparator: true,
    });
  }

  return actions;
}

interface RegistrantUserActionHandlers {
  onBilling?: () => void;
  onMakePrimary?: (event: React.MouseEvent) => void;
  onDisable?: (event: React.MouseEvent) => void;
  onReenable?: (event: React.MouseEvent) => void;
  onCancelInvite?: (event: React.MouseEvent) => void;
  busy?: Partial<Record<RegistrantUserActionKey, boolean>>;
}

function invokeAction(
  action: RegistrantUserAction,
  handlers: RegistrantUserActionHandlers,
  event: React.MouseEvent,
): void {
  switch (action.key) {
    case "billing":
      handlers.onBilling?.();
      return;
    case "makePrimary":
      handlers.onMakePrimary?.(event);
      return;
    case "disable":
      handlers.onDisable?.(event);
      return;
    case "reenable":
      handlers.onReenable?.(event);
      return;
    case "cancelInvite":
      handlers.onCancelInvite?.(event);
  }
}

export function RegistrantUserActionsMenu({
  user,
  paymentAccountId,
  omit,
  ...handlers
}: RegistrantUserActionsModelProps & RegistrantUserActionHandlers) {
  const actions = getRegistrantUserActions({ user, paymentAccountId, omit });

  return (
    <>
      {actions.map((action) => (
        <Fragment key={action.key}>
          {action.beforeSeparator ? <DropdownMenuSeparator /> : null}
          <DropdownMenuItem
            variant={action.variant}
            disabled={handlers.busy?.[action.key]}
            onClick={(event) => invokeAction(action, handlers, event)}
          >
            <action.icon className="h-4 w-4" />
            {action.label}
          </DropdownMenuItem>
        </Fragment>
      ))}
    </>
  );
}

export function RegistrantUserActionButtons({
  user,
  paymentAccountId,
  omit,
  ...handlers
}: RegistrantUserActionsModelProps & RegistrantUserActionHandlers) {
  const actions = getRegistrantUserActions({ user, paymentAccountId, omit });

  if (actions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((action) => (
        <Button
          key={action.key}
          variant={action.variant === "destructive" ? "destructive" : "outline"}
          size="sm"
          disabled={handlers.busy?.[action.key]}
          onClick={(event) => invokeAction(action, handlers, event)}
        >
          <action.icon className="h-4 w-4" />
          {action.label}
        </Button>
      ))}
    </div>
  );
}
